/**
 * O MÓDULO DE GRUPOS — um lugar só, nas quatro rotas dele (`/publicacoes`, `/gatilhos`, `/portas`, `/lista-negra`).
 *
 * ⚠️ POR QUE (owner, 01/11, depois do H128 no ar): *"Vendas → Grupos tá ruim, confusa, desorganizada. Nada intuitiva;
 * quando navego por suas abas, o posicionamento muda."* Publicações tinha cabeçalho ("Grupos" + Nova publicação) e
 * depois as abas; Gatilhos, Portas e Lista negra mostravam as abas SEM cabeçalho e uma lista com outro título — o topo
 * pulava de lugar a cada troca. Agora as quatro rotas usam o MESMO esqueleto (`ModuloDeGrupos`):
 *
 *     [ícone] Grupos (i)                                         [a ação primária DA ABA, quando a aba tem]
 *     Visão geral · Publicações · Seus grupos · Automático · Lista negra
 *     [o conteúdo da aba, na mesma área elástica]
 *
 * O cabeçalho tem altura fixa (com ou sem botão) e as abas ficam sempre logo abaixo: nada do topo se move ao trocar.
 * Quem prova: `modulo-de-grupos.test.tsx` (mesmo esqueleto nas quatro) e a medição com Playwright no handoff.
 *
 * ## As cinco abas, pela tarefa (eram sete)
 *
 * - **Visão geral** — o que pede ação agora (falhas, o que sai, o que saiu).
 * - **Publicações** — Lista | Calendário (dois modos da mesma aba; `?aba=calendario` abre no calendário).
 * - **Seus grupos** — Grupos | Canais do WhatsApp. A antiga aba "Canais" NÃO é a tela de Canais do menu (lá são as
 *   conexões/números); aqui são os *canais de difusão* do WhatsApp — audiência, como os grupos. Por isso virou um modo
 *   de "Seus grupos", e não uma aba que parecia repetir o menu. `?aba=canais` continua abrindo.
 * - **Automático** — Gatilhos | Portas de entrada: o que acontece sozinho quando alguém entra, sai ou clica no link.
 *   `/gatilhos` e `/portas` continuam sendo os endereços dos dois modos.
 * - **Lista negra** — quem não fica nos grupos.
 */
import { useEffect, type ReactNode } from "react";
import { CabecalhoDaTela, PaginaInteira, Segmentado } from "@hugi/ui/kit";
import { UsersRound } from "lucide-react";
import { useLocation } from "wouter";
import "./hugi-grupos.css";

/** As seções que moram em `/publicacoes?aba=…` (trocam no lugar, sem sair da página). */
export type SecaoDePublicacoes = "inicio" | "publicacoes" | "calendario" | "grupos" | "canais";
/** As cinco abas do módulo. */
export type AbaDoModulo = "inicio" | "publicacoes" | "grupos" | "automatico" | "lista-negra";

export const TITULO_DO_MODULO = "Grupos";
const FRASE = "Sincronize os grupos do número, agende o que sai em cada um, e deixe no automático o que acontece quando alguém entra, sai ou clica no link.";

export const ABAS_DO_MODULO: readonly { id: AbaDoModulo; rotulo: string; href: string; daPagina?: SecaoDePublicacoes }[] = [
  { id: "inicio", rotulo: "Visão geral", href: "/publicacoes", daPagina: "inicio" },
  { id: "publicacoes", rotulo: "Publicações", href: "/publicacoes?aba=publicacoes", daPagina: "publicacoes" },
  { id: "grupos", rotulo: "Seus grupos", href: "/publicacoes?aba=grupos", daPagina: "grupos" },
  { id: "automatico", rotulo: "Automático", href: "/gatilhos" },
  { id: "lista-negra", rotulo: "Lista negra", href: "/lista-negra" },
];

/** A aba do módulo em que cada seção de `/publicacoes` mora. */
export function abaDaSecao(s: SecaoDePublicacoes): AbaDoModulo {
  return s === "calendario" ? "publicacoes" : s === "canais" ? "grupos" : s;
}

export const ID_DO_PAINEL_DO_MODULO = "hugi-grupos-painel";

export function AbasDoModulo(props: {
  readonly ativa: AbaDoModulo;
  /** Só em `/publicacoes`: troca a seção sem sair da página. */
  readonly aoTrocarNaPagina?: (secao: SecaoDePublicacoes) => void;
}) {
  const [, navegar] = useLocation();
  // No celular as cinco abas rolam de lado: a acesa vem para a vista (Lista negra, a última, abria escondida). Só o
  // `scrollLeft` da própria fileira — `scrollIntoView` poderia rolar a página, que não rola.
  useEffect(() => {
    const aba = document.querySelector<HTMLElement>('.hugi-pub-abas [aria-selected="true"]');
    const fileira = aba?.parentElement;
    if (!aba || !fileira || fileira.scrollWidth <= fileira.clientWidth) return;
    fileira.scrollLeft = Math.max(0, aba.offsetLeft - fileira.offsetLeft - 8);
  }, [props.ativa]);
  return (
    <Segmentado
      classe="hugi-pub-abas"
      rotulo="Seções de Grupos"
      ativa={props.ativa}
      aoTrocar={(id) => {
        const aba = ABAS_DO_MODULO.find((a) => a.id === id)!;
        if (props.aoTrocarNaPagina && aba.daPagina) props.aoTrocarNaPagina(aba.daPagina);
        else navegar(aba.href);
      }}
      opcoes={ABAS_DO_MODULO.map((a) => ({ id: a.id, rotulo: a.rotulo, controla: ID_DO_PAINEL_DO_MODULO }))}
    />
  );
}

/** O esqueleto das quatro rotas: o mesmo cabeçalho, as abas logo abaixo, e a área da aba. */
export function ModuloDeGrupos(props: {
  readonly ativa: AbaDoModulo;
  readonly primaria?: ReactNode;
  readonly aoTrocarNaPagina?: (secao: SecaoDePublicacoes) => void;
  readonly children: ReactNode;
}) {
  const rotulo = ABAS_DO_MODULO.find((a) => a.id === props.ativa)?.rotulo ?? TITULO_DO_MODULO;
  return (
    <PaginaInteira rotulo={TITULO_DO_MODULO} classe="hugi-modulo-grupos">
      <CabecalhoDaTela
        icone={<UsersRound size={18} />}
        titulo={TITULO_DO_MODULO}
        dica={FRASE}
        classe="hugi-modulo-grupos-cabecalho"
        {...(props.primaria ? { primaria: props.primaria } : {})}
      />
      <AbasDoModulo ativa={props.ativa} {...(props.aoTrocarNaPagina ? { aoTrocarNaPagina: props.aoTrocarNaPagina } : {})} />
      <div id={ID_DO_PAINEL_DO_MODULO} role="tabpanel" aria-label={rotulo} className="v2-elastico hugi-pub-aba">
        {props.children}
      </div>
    </PaginaInteira>
  );
}

/**
 * O MODO de uma aba (Lista | Calendário, Grupos | Canais, Gatilhos | Portas): botões pequenos, sempre no começo das
 * ferramentas do cartão da aba — o mesmo lugar nas três.
 */
export function ModoDaAba<T extends string>(props: {
  readonly rotulo: string;
  readonly ativo: T;
  readonly opcoes: readonly { readonly id: T; readonly rotulo: string; readonly icone?: ReactNode }[];
  readonly aoTrocar: (id: T) => void;
}) {
  return (
    <span className="hugi-mini-acoes hugi-modo-da-aba" role="group" aria-label={props.rotulo}>
      {props.opcoes.map((o) => (
        <button
          key={o.id}
          type="button"
          className={`v2-botao is-pequeno${props.ativo === o.id ? " is-ligado" : ""}`}
          aria-pressed={props.ativo === o.id}
          onClick={() => props.aoTrocar(o.id)}
        >
          {o.icone}{o.icone ? " " : ""}{o.rotulo}
        </button>
      ))}
    </span>
  );
}

/** Gatilhos | Portas de entrada — os dois modos da aba Automático, cada um com o seu endereço. */
export function ModoDoAutomatico(props: { readonly ativo: "gatilhos" | "portas" }) {
  const [, navegar] = useLocation();
  return (
    <ModoDaAba
      rotulo="Automático: ver"
      ativo={props.ativo}
      opcoes={[{ id: "gatilhos", rotulo: "Gatilhos" }, { id: "portas", rotulo: "Portas de entrada" }]}
      aoTrocar={(id) => navegar(id === "gatilhos" ? "/gatilhos" : "/portas")}
    />
  );
}
